import { DIRECT_LINE_POST_ACTIVITY } from 'constants/webchat/activityNames'
import { EMPTY_FILE, READ_FILE_FAILED, UPLOAD_FAILED, UPLOAD_TIMED_OUT } from 'constants/webchat/uploadErrorCodes'

export const sendFilesToChatbot = async (dispatch, action, uploadUrl, logsUrl, token = null, showToast = null, t = null) => {
  let uploadedAttachments = []
  let hasFailure = false
  for (let attachment of action.payload.attachments) {
    const { thumbnailURL, blob } = attachment
    const { name, size, type } = blob
    let { contentUrl, errorKey } = await uploadFileToChatbot(name, blob, uploadUrl, logsUrl, token)
    if (contentUrl) {
      let attachment = {
        contentUrl,
        name,
        contentType: type
      }
      if (thumbnailURL) {
        attachment.thumbnailUrl = thumbnailURL.href
      }
      uploadedAttachments.push({ attachment, size })
    } else {
      hasFailure = true
      if (errorKey && showToast && t) {
        const translationKey = mapErrorCodeToTranslationKey(errorKey)
        showToast(t(translationKey, { name }), 'error', { autoClose: false })
      }
    }
  }

  if (hasFailure) return

  const activity = {
    name: 'webchat/upload',
    type: 'message',
    attachments: null,
    attachmentsWebchat: uploadedAttachments.map(({ attachment }) => attachment),
    channelData: {
      attachmentSizes: uploadedAttachments.map(({ size }) => size)
    }
  }
  dispatch({
    type: DIRECT_LINE_POST_ACTIVITY,
    meta: { method: 'keyboard' },
    payload: {
      activity
    }
  })
}

export const uploadFileToChatbot = async (name, blob, uploadUrl, logsUrl, token = null) => {
  let headers = {
    'Content-Type': 'application/octet-stream'
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  let fileArrayBuffer
  try {
    fileArrayBuffer = await blob.arrayBuffer()
  } catch (error) {
    console.error('Failed to get blob array buffer.', error)
    const log = {
      level: 'error',
      message: `Failed to get blob array buffer for file: ${name}.\nError: ${error}`
    }
    if (token) {
      logWebchatHost(logsUrl, token, log)
    }
    return { contentUrl: null, errorKey: 'Chatbot.Upload.ReadFileFailed' }
  }

  try {
    const uploadResponse = await fetch(`${uploadUrl}?name=${name}`, {
      method: 'POST',
      headers: headers,
      body: fileArrayBuffer
    })
    if (!uploadResponse.ok) {
      const errorObject = await uploadResponse.json()
      const log = {
        level: 'error',
        message: `Failed to upload file: ${name}.\nHTTP Response: Status: ${uploadResponse.status}, StatusText: ${
          uploadResponse.statusText
        }, Ok: ${uploadResponse.ok}, Headers: ${JSON.stringify(uploadResponse.headers, null)}, URL: ${uploadResponse.url}, Type: ${
          uploadResponse.type
        }, BodyUsed: ${uploadResponse.bodyUsed}, Redirected: ${uploadResponse.redirected}`
      }
      if (token) {
        logWebchatHost(logsUrl, token, log)
      }
      return { contentUrl: null, errorKey: errorObject?.error || UPLOAD_FAILED }
    } else {
      const result = await uploadResponse.json()
      return { contentUrl: result?.uploadUrl, errorKey: null }
    }
  } catch (error) {
    console.error('Failed to upload file to chatbot.', error)
    const log = {
      level: 'error',
      message: `Failed to upload file to chatbot: ${name}.\nError: ${error}`,
      timestamp: new Date().toISOString()
    }
    if (token) {
      logWebchatHost(logsUrl, token, log)
    }
    return { contentUrl: null, errorKey: UPLOAD_FAILED }
  }
}

export const moveAttachmentsWebchatToAttachments = activity => {
  activity.attachments = activity.attachmentsWebchat
  delete activity.attachmentsWebchat
  delete activity.name
}

export const extractContentTypesFromBase64 = baseString => baseString.match(/[^:\s*]\w+\/[\w-+\d.]+(?=[;| ])/)[0]

export const logWebchatHost = (logsUrl, token, log) => {
  // Fire and forget logging because we don't want to block the user experience.
  fetch(logsUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify([log])
  }).catch(err => {
    console.error('Failed to log file upload.', err)
  })
}

const mapErrorCodeToTranslationKey = key => {
  const map = {
    [UPLOAD_FAILED]: 'Chatbot.Upload.UploadFailed',
    [UPLOAD_TIMED_OUT]: 'Chatbot.Upload.UploadTimedOut',
    [READ_FILE_FAILED]: 'Chatbot.Upload.ReadFileFailed',
    [EMPTY_FILE]: 'Chatbot.Upload.EmptyFile'
  }
  return map[key] || 'Chatbot.Upload.UploadFailed'
}
